import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { FullPageLoader } from "@common/components/Loading/FullPageLoading";
import {
  addTimezoneOffsetToUTCZeroTimeIfNeeded,
  formatDate,
} from "@common/components/utils";
import {
  ExpenseStatus,
  StipendExpenseStatus,
  Transaction,
  TransactionStatus,
  TransactionType,
  useGetDebitTransactionsQuery,
} from "@common/utils/graphql";
import { Body } from "@hypercard/web-components";
import { useEffect, useState } from "react";
import { ExpenseFormValues } from "../forms/Forms";
import { cn } from "@/utils/utils";
import { StatusText } from "@common/components/StatusText";

const isStatusPendingOrApproved = (
  status?: ExpenseStatus | StipendExpenseStatus,
) =>
  status === ExpenseStatus.Approved ||
  status === ExpenseStatus.Pending ||
  status === StipendExpenseStatus.Approved ||
  status === StipendExpenseStatus.Pending;

const getLatestTransactionExpense = (tx: Transaction) => {
  if (tx.latestExpense && tx.latestStipendExpense) {
    return tx.latestExpense.createdAt > tx.latestStipendExpense.createdAt
      ? tx.latestExpense
      : tx.latestStipendExpense;
  } else if (tx.latestExpense) {
    return tx.latestExpense;
  } else if (tx.latestStipendExpense) {
    return tx.latestStipendExpense;
  }
  return null;
};

type Props = {
  onSelection: (tx: Partial<ExpenseFormValues>) => void;
};

export const HypercardTransactionSelection = ({ onSelection }: Props) => {
  const [merchantName, setMerchantName] = useState<string>("");
  const { data, loading } = useGetDebitTransactionsQuery({
    variables: {
      status: [TransactionStatus.Settled, TransactionStatus.Pending],
      merchantName: merchantName.length > 0 ? merchantName : undefined,
      type: [TransactionType.Regular],
    },
    fetchPolicy: "cache-and-network",
  });

  const { toast } = useToast();
  const handleClick = (tx: Transaction) => () => {
    onSelection({
      amount: tx.amountInDollars,
      merchant: tx.displayTitle,
      date: addTimezoneOffsetToUTCZeroTimeIfNeeded(tx.transactedAt),
      transactionId: tx.id,
      expenseCategoryGLId: tx.expenseCategoryGLId
        ? String(tx.expenseCategoryGLId)
        : undefined,
    });
  };

  useEffect(() => {
    if (
      !loading &&
      data?.transactions.__typename !== "QueryTransactionsSuccess"
    ) {
      toast({
        variant: "destructive",
        title: "Failed to load transactions, please try again later.",
      });
    }
  }, [data?.transactions.__typename, toast, loading]);

  const transactions =
    data?.transactions.__typename === "QueryTransactionsSuccess"
      ? (data.transactions.data.allTransactions.filter(
          (tx) => tx.__typename === "Transaction",
        ) as Transaction[])
      : ([] as Transaction[]);

  return (
    <div className="flex flex-1 flex-col">
      <div className="flex space-x-4">
        <Input
          className="flex-1"
          type="search"
          placeholder="Search..."
          onChange={(e) => setMerchantName(e.target.value)}
          value={merchantName}
        />
      </div>
      {loading && transactions.length === 0 ? (
        <div className="grow pb-20">
          <FullPageLoader />
        </div>
      ) : transactions.length === 0 ? (
        <div className="mt-10 flex w-full justify-center">
          <Body size="md" weight="regular" className="italic text-white/70">
            No transactions found
          </Body>
        </div>
      ) : (
        <div className="mt-4 flex flex-1 flex-col space-y-3">
          {transactions.map((tx) => {
            const latestExpense = getLatestTransactionExpense(tx);
            const isDisabled = isStatusPendingOrApproved(latestExpense?.status);
            const reportOrStipendStatus = latestExpense?.status;

            let reportOrStipendTitle: string | undefined;
            if (latestExpense?.__typename === "Expense") {
              reportOrStipendTitle = latestExpense.report.title;
            } else if (latestExpense?.__typename === "StipendExpense") {
              reportOrStipendTitle = latestExpense.stipendName;
            }

            return (
              <button
                disabled={isDisabled}
                className={cn(
                  "flex items-center space-x-4 rounded-md px-4 py-2 text-left transition-colors",
                  isDisabled
                    ? "cursor-not-allowed opacity-50"
                    : "hover:bg-white/5",
                )}
                key={`select-transaction--transaction-${tx.id}`}
                onClick={handleClick(tx)}
              >
                {/* Title and date */}
                <div className="flex-1 space-y-0.5 overflow-hidden">
                  <Body size="md" weight="medium" className="truncate">
                    {tx.displayTitle}
                  </Body>
                  <Body size="sm" weight="regular" className="text-white/70">
                    {reportOrStipendTitle
                      ? reportOrStipendTitle
                      : formatDate(tx.transactedAt)}
                  </Body>
                </div>

                {/* Amount and status */}
                <div className="flex h-full flex-col gap-y-0.5">
                  <Body size="md" weight="medium">
                    {tx.displayDollarAmount}
                  </Body>
                  {reportOrStipendStatus && (
                    <StatusText size="sm" status={reportOrStipendStatus} />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
